import React, { useState, useEffect, useCallback } from 'react';
import {
    AiOutlinePlus,
    AiOutlineSearch,
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineDollar,
    AiOutlineCalendar,
    AiOutlineEye,
    AiOutlineClockCircle,
    AiOutlineCheckCircle,
    AiOutlineExclamationCircle,
    AiOutlineClose,
} from 'react-icons/ai';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './Expense.module.css';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactLoading from 'react-loading';
import Moment from 'moment';
import { Pagination } from '@mui/material';
import { useBusinessSettings } from '../../context/BusinessSettingsContext';

function Expense() {
    const { isFeatureEnabled } = useBusinessSettings();
    const uid = localStorage.getItem('id');

    // Date filters
    const [DateFrom, setDateFrom] = useState(new Date());
    DateFrom.setHours(0, 0, 0, 0);
    const [DateTo, setDateTo] = useState(new Date());
    DateTo.setHours(0, 0, 0, 0);
    const [DateSearch, setDateSearch] = useState(false);

    // Data state
    const [expenses, setExpenses] = useState([]);
    const [ExpenseAmount, setExpenseAmount] = useState(0);
    const [ExpenseLAmount, setExpenseLAmount] = useState(0);
    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(false);

    // Search and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [searchDebounce, setSearchDebounce] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [expensesPerPage, setExpensesPerPage] = useState(10);

    // Modal states
    const [showViewModal, setShowViewModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [deletingExpense, setDeletingExpense] = useState(null);

    // Form states for create
    const [createForm, setCreateForm] = useState({
        Title: '',
        Amount: '',
        ExpenseId: '',
        Description: '',
        ExpDate: new Date(),
        paymentMethod: 'cash',
        paymentStatus: 'paid',
    });

    // Form states for edit
    const [editForm, setEditForm] = useState({
        Title: '',
        Amount: '',
        ExpenseId: '',
        Description: '',
        paymentMethod: 'cash',
        paymentStatus: 'paid',
    });

    const fetchExpenses = useCallback(
        async (pageNum = 1, search = '', dateSearch = false, dateFrom = null, dateTo = null) => {
            setLoading(true);
            try {
                const response = await AxiosUrl.get('/search-expenses', {
                    params: {
                        uid,
                        searchQuery: search,
                        page: pageNum,
                        limit: expensesPerPage,
                        DateFrom: dateSearch && dateFrom ? dateFrom.toISOString() : undefined,
                        DateTo: dateSearch && dateTo ? dateTo.toISOString() : undefined,
                        DateSearch: dateSearch,
                    },
                });

                if (response.data.success) {
                    setExpenses(response.data.data);
                    setTotalPages(response.data.pagination.totalPages);
                    setTotalCount(response.data.pagination.totalCount);
                    setExpenseAmount(response.data.summary.thisMonth);
                    setExpenseLAmount(response.data.summary.lastMonth);
                }
            } catch (error) {
                console.error('Error fetching expenses:', error);
                toast.error('Failed to load expenses');
            } finally {
                setLoading(false);
            }
        },
        [uid, expensesPerPage]
    );

    useEffect(() => {
        fetchExpenses(page, searchTerm, DateSearch, DateFrom, DateTo);
    }, [refresh, page, expensesPerPage]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (searchDebounce) clearTimeout(searchDebounce);
        const timeout = setTimeout(() => {
            setPage(1);
            fetchExpenses(1, value, DateSearch, DateFrom, DateTo);
        }, 300);
        setSearchDebounce(timeout);
    };

    const clearSearch = () => {
        setSearchTerm('');
        setPage(1);
        fetchExpenses(1, '', DateSearch, DateFrom, DateTo);
    };

    const handleDateSearch = () => {
        setDateSearch(true);
        setPage(1);
        fetchExpenses(1, searchTerm, true, DateFrom, DateTo);
    };

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePerPageChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setExpensesPerPage(newLimit);
        setPage(1);
    };

    // View modal handlers
    const openViewModal = (expense) => {
        setSelectedExpense(expense);
        setShowViewModal(true);
    };

    const closeViewModal = () => {
        setShowViewModal(false);
        setSelectedExpense(null);
    };

    // Create modal handlers
    const openCreateModal = () => {
        setCreateForm({
            Title: '',
            Amount: '',
            ExpenseId: '',
            Description: '',
            ExpDate: new Date(),
            paymentMethod: 'cash',
            paymentStatus: 'paid',
        });
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setCreateForm({
            Title: '',
            Amount: '',
            ExpenseId: '',
            Description: '',
            ExpDate: new Date(),
            paymentMethod: 'cash',
            paymentStatus: 'paid',
        });
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (createForm.ExpDate > new Date()) {
            toast.error("You can set previous date but can't set any future date");
            return;
        }

        if (!createForm.Title || !createForm.Amount || !createForm.ExpenseId || !createForm.Description) {
            toast.error('Title, Amount, Expense ID, and Description are required');
            return;
        }

        try {
            const fromData = {
                uid,
                ...createForm,
                ...(isFeatureEnabled('enableCashFlow') && {
                    paymentMethod: createForm.paymentMethod,
                    paymentStatus: createForm.paymentStatus,
                    cashImpact: createForm.paymentStatus === 'paid' ? -parseFloat(createForm.Amount || 0) : 0,
                }),
            };

            const response = await AxiosUrl.post(`/add-expense`, fromData);

            if (response.data.success) {
                toast.success('Expense added successfully');
                closeCreateModal();
                setRefresh((prev) => prev + 1);
            } else {
                toast.error(response.data.message || 'Failed to add expense');
            }
        } catch (error) {
            console.error(error);
            toast.error('Error adding expense');
        }
    };

    // Edit modal handlers
    const openEditModal = (expense) => {
        setSelectedExpense(expense);
        setEditForm({
            Title: expense.Title,
            Amount: expense.Amount,
            ExpenseId: expense.ExpenseId,
            Description: expense.Description,
            paymentMethod: expense.paymentMethod || 'cash',
            paymentStatus: expense.paymentStatus || 'paid',
        });
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setSelectedExpense(null);
        setEditForm({
            Title: '',
            Amount: '',
            ExpenseId: '',
            Description: '',
            paymentMethod: 'cash',
            paymentStatus: 'paid',
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!editForm.Title || !editForm.Amount || !editForm.ExpenseId || !editForm.Description) {
            toast.error('Title, Amount, ExpenseId, Description is Required..');
            return;
        }

        try {
            const fromData = {
                uid,
                ...editForm,
                ExId: selectedExpense._id,
                ...(isFeatureEnabled('enableCashFlow') && {
                    paymentMethod: editForm.paymentMethod,
                    paymentStatus: editForm.paymentStatus,
                    cashImpact: editForm.paymentStatus === 'paid' ? -parseFloat(editForm.Amount || 0) : 0,
                }),
            };

            await AxiosUrl.post(`/edit-expense`, fromData);
            toast.success('Expense updated successfully');
            closeEditModal();
            setRefresh((prev) => prev + 1);
        } catch (error) {
            console.error(error);
            toast.error('Failed to update expense');
        }
    };

    // Delete modal handlers
    const openDeleteModal = (expense) => {
        setDeletingExpense(expense);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setDeletingExpense(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deletingExpense) return;

        try {
            const response = await AxiosUrl.post(`/delete-expense/${uid}`, { id: deletingExpense._id });
            toast.success('Expense deleted successfully');
            closeDeleteModal();
            setRefresh((prev) => prev + 1);
        } catch (error) {
            toast.error('Failed to delete the expense.');
            console.error(error);
        }
    };

    const formatCurrency = (amount) => {
        return `৳${parseFloat(amount || 0).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })}`;
    };

    return (
        <>
            <ToastContainer />
            <div className={styles.expenseContainer}>
                {/* Header Section */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <h1 className={styles.pageTitle}>Expense Management</h1>
                        <p className={styles.pageSubtitle}>
                            Track and manage your business expenses
                        </p>
                    </div>
                    <div className={styles.headerRight}>
                        <button
                            className={styles.primaryButton}
                            onClick={openCreateModal}
                        >
                            <AiOutlinePlus className={styles.buttonIcon} />
                            Add Expense
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className={styles.statsGrid}>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>
                            <AiOutlineDollar />
                        </div>
                        <div className={styles.statContent}>
                            <div className={styles.statValue}>
                                {formatCurrency(ExpenseAmount)}
                            </div>
                            <div className={styles.statLabel}>This Month</div>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>
                            <AiOutlineCalendar />
                        </div>
                        <div className={styles.statContent}>
                            <div className={styles.statValue}>
                                {formatCurrency(ExpenseLAmount)}
                            </div>
                            <div className={styles.statLabel}>Last Month</div>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>
                            <AiOutlineClockCircle />
                        </div>
                        <div className={styles.statContent}>
                            <div className={styles.statValue}>{totalCount}</div>
                            <div className={styles.statLabel}>Total Expenses</div>
                        </div>
                    </div>
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>
                            <AiOutlineCheckCircle />
                        </div>
                        <div className={styles.statContent}>
                            <div className={styles.statValue}>
                                {formatCurrency(ExpenseAmount - ExpenseLAmount)}
                            </div>
                            <div className={styles.statLabel}>Difference</div>
                        </div>
                    </div>
                </div>

                {/* Date Range Filter */}
                <div className={styles.dateFilterSection}>
                    <div className={styles.dateFilterTitle}>
                        <AiOutlineCalendar className={styles.filterIcon} />
                        Filter by Date Range
                    </div>
                    <div className={styles.datePickerContainer}>
                        <div className={styles.datePickerWrapper}>
                            <label className={styles.dateLabel}>From:</label>
                            <DatePicker
                                selected={DateFrom}
                                onChange={(date) => setDateFrom(date)}
                                className={styles.datePicker}
                            />
                        </div>
                        <div className={styles.datePickerWrapper}>
                            <label className={styles.dateLabel}>To:</label>
                            <DatePicker
                                selected={DateTo}
                                onChange={(date) => setDateTo(date)}
                                className={styles.datePicker}
                            />
                        </div>
                        <button
                            className={styles.searchButton}
                            onClick={handleDateSearch}
                        >
                            <AiOutlineSearch className={styles.searchIcon} />
                            Search
                        </button>
                        {DateSearch && (
                            <button
                                className={styles.clearButton}
                                onClick={() => {
                                    setDateSearch(false);
                                    setPage(1);
                                    fetchExpenses(1, searchTerm, false, null, null);
                                }}
                            >
                                Clear Filter
                            </button>
                        )}
                    </div>
                </div>

                {/* Search and Filters */}
                <div className={styles.filtersSection}>
                    <div className={styles.searchBox}>
                        <input
                            type="text"
                            placeholder="Search by title, ID, or description..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            className={styles.searchInput}
                        />
                        <AiOutlineSearch className={styles.searchIconInside} />
                        {searchTerm && (
                            <button className={styles.clearBtn} onClick={clearSearch}>
                                <AiOutlineClose />
                            </button>
                        )}
                    </div>
                    <div className={styles.filterControls}>
                        <div className={styles.perPageSelector}>
                            <label className={styles.perPageLabel}>Show:</label>
                            <select
                                value={expensesPerPage}
                                onChange={handlePerPageChange}
                                className={styles.perPageSelect}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                        <div className={styles.expenseCount}>
                            {totalCount} expenses
                        </div>
                    </div>
                </div>

                {/* Expenses Table */}
                <div className={styles.tableContainer}>
                    <div className={styles.tableHeader}>
                        <div className={styles.tableTitle}>
                            {DateSearch
                                ? `Expenses from ${Moment(DateFrom).format('DD MMM YYYY')} to ${Moment(DateTo).format('DD MMM YYYY')}`
                                : 'Recent Expenses'}
                        </div>
                    </div>

                    <div className={styles.tableWrapper}>
                        {loading ? (
                            <div className={styles.loadingContainer}>
                                <ReactLoading
                                    type="cylon"
                                    color="#008060"
                                    height={60}
                                    width={60}
                                />
                                <p className={styles.loadingText}>
                                    Loading expenses...
                                </p>
                            </div>
                        ) : (
                            <>
                                <table className={styles.expenseTable}>
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Title</th>
                                            <th>Amount</th>
                                            {isFeatureEnabled('enableCashFlow') && (
                                                <>
                                                    <th>Payment Method</th>
                                                    <th>Payment Status</th>
                                                </>
                                            )}
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {expenses.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={isFeatureEnabled('enableCashFlow') ? "6" : "4"}
                                                    className={styles.emptyRow}
                                                >
                                                    <div className={styles.emptyState}>
                                                        <div className={styles.emptyIcon}>
                                                            <AiOutlineExclamationCircle />
                                                        </div>
                                                        <h3 className={styles.emptyTitle}>
                                                            No expenses found
                                                        </h3>
                                                        <p className={styles.emptyDescription}>
                                                            {searchTerm || DateSearch
                                                                ? 'Try adjusting your search criteria'
                                                                : 'Get started by adding your first expense'}
                                                        </p>
                                                        {!searchTerm && !DateSearch && (
                                                            <button
                                                                className={styles.emptyActionButton}
                                                                onClick={openCreateModal}
                                                            >
                                                                <AiOutlinePlus />
                                                                Add Expense
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            expenses.map((expense) => (
                                                <tr
                                                    key={expense._id}
                                                    className={styles.tableRow}
                                                >
                                                    <td className={styles.dateCell}>
                                                        <div className={styles.dateValue}>
                                                            {Moment(expense.Date).format('DD MMM YYYY')}
                                                        </div>
                                                    </td>
                                                    <td className={styles.titleCell}>
                                                        <div className={styles.titleValue}>
                                                            {expense.Title}
                                                        </div>
                                                    </td>
                                                    <td className={styles.amountCell}>
                                                        <div className={styles.amountValue}>
                                                            {formatCurrency(expense.Amount)}
                                                        </div>
                                                    </td>
                                                    {isFeatureEnabled('enableCashFlow') && (
                                                        <>
                                                            <td className={styles.paymentCell}>
                                                                <span className={styles.paymentBadge}>
                                                                    {expense.paymentMethod
                                                                        ? expense.paymentMethod.charAt(0).toUpperCase() +
                                                                          expense.paymentMethod.slice(1)
                                                                        : '-'}
                                                                </span>
                                                            </td>
                                                            <td className={styles.statusCell}>
                                                                <span
                                                                    className={`${styles.statusBadge} ${
                                                                        expense.paymentStatus === 'paid'
                                                                            ? styles.statusPaid
                                                                            : expense.paymentStatus === 'unpaid'
                                                                            ? styles.statusUnpaid
                                                                            : styles.statusPartial
                                                                    }`}
                                                                >
                                                                    {expense.paymentStatus
                                                                        ? expense.paymentStatus.charAt(0).toUpperCase() +
                                                                          expense.paymentStatus.slice(1).replace('_', ' ')
                                                                        : '-'}
                                                                </span>
                                                            </td>
                                                        </>
                                                    )}
                                                    <td className={styles.actionsCell}>
                                                        <div className={styles.actionButtons}>
                                                            <button
                                                                className={styles.viewButton}
                                                                onClick={() => openViewModal(expense)}
                                                                title="View Details"
                                                            >
                                                                <AiOutlineEye />
                                                            </button>
                                                            <button
                                                                className={styles.editButton}
                                                                onClick={() => openEditModal(expense)}
                                                                title="Edit"
                                                            >
                                                                <AiOutlineEdit />
                                                            </button>
                                                            <button
                                                                className={styles.deleteButton}
                                                                onClick={() => openDeleteModal(expense)}
                                                                title="Delete"
                                                            >
                                                                <AiOutlineDelete />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className={styles.paginationContainer}>
                                        <Pagination
                                            count={totalPages}
                                            page={page}
                                            onChange={handlePageChange}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    color: '#202223',
                                                },
                                                '& .Mui-selected': {
                                                    backgroundColor: '#008060 !important',
                                                    color: 'white',
                                                },
                                                '& .MuiPaginationItem-root:hover': {
                                                    backgroundColor: '#f6f6f7',
                                                },
                                            }}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* View Expense Modal */}
            {showViewModal && selectedExpense && (
                <div className={styles.modalOverlay} onClick={closeViewModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Expense Details</h2>
                            <button className={styles.closeBtn} onClick={closeViewModal}>
                                <AiOutlineClose />
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <div className={styles.detailRow}>
                                <label>Expense ID:</label>
                                <span>{selectedExpense.ExpenseId}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <label>Title:</label>
                                <span>{selectedExpense.Title}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <label>Amount:</label>
                                <span className={styles.amountHighlight}>
                                    {formatCurrency(selectedExpense.Amount)}
                                </span>
                            </div>
                            <div className={styles.detailRow}>
                                <label>Date:</label>
                                <span>{Moment(selectedExpense.Date).format('DD MMM YYYY, hh:mm A')}</span>
                            </div>
                            <div className={styles.detailRow}>
                                <label>Description:</label>
                                <span>{selectedExpense.Description}</span>
                            </div>
                            {isFeatureEnabled('enableCashFlow') && (
                                <>
                                    <div className={styles.detailRow}>
                                        <label>Payment Method:</label>
                                        <span className={styles.paymentBadge}>
                                            {selectedExpense.paymentMethod
                                                ? selectedExpense.paymentMethod.charAt(0).toUpperCase() +
                                                  selectedExpense.paymentMethod.slice(1)
                                                : '-'}
                                        </span>
                                    </div>
                                    <div className={styles.detailRow}>
                                        <label>Payment Status:</label>
                                        <span
                                            className={`${styles.statusBadge} ${
                                                selectedExpense.paymentStatus === 'paid'
                                                    ? styles.statusPaid
                                                    : selectedExpense.paymentStatus === 'unpaid'
                                                    ? styles.statusUnpaid
                                                    : styles.statusPartial
                                            }`}
                                        >
                                            {selectedExpense.paymentStatus
                                                ? selectedExpense.paymentStatus.charAt(0).toUpperCase() +
                                                  selectedExpense.paymentStatus.slice(1).replace('_', ' ')
                                                : '-'}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Create Expense Modal */}
            {showCreateModal && (
                <div className={styles.modalOverlay} onClick={closeCreateModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Add New Expense</h2>
                            <button className={styles.closeBtn} onClick={closeCreateModal}>
                                <AiOutlineClose />
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubmit} className={styles.modalForm}>
                            <div className={styles.formGroup}>
                                <label>Expense Title *</label>
                                <input
                                    type="text"
                                    value={createForm.Title}
                                    onChange={(e) => setCreateForm({ ...createForm, Title: e.target.value })}
                                    placeholder="Example: Office Rent"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Expense Amount *</label>
                                <input
                                    type="number"
                                    value={createForm.Amount}
                                    onChange={(e) => setCreateForm({ ...createForm, Amount: e.target.value })}
                                    placeholder="Example: 5000"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Expense ID *</label>
                                <input
                                    type="text"
                                    value={createForm.ExpenseId}
                                    onChange={(e) => setCreateForm({ ...createForm, ExpenseId: e.target.value })}
                                    placeholder="Example: INV-2024-001"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Description *</label>
                                <textarea
                                    value={createForm.Description}
                                    onChange={(e) => setCreateForm({ ...createForm, Description: e.target.value })}
                                    placeholder="Example: Monthly office rent payment"
                                    rows={3}
                                    required
                                />
                            </div>

                            {isFeatureEnabled('enableCashFlow') && (
                                <>
                                    <div className={styles.formGroup}>
                                        <label>Payment Method</label>
                                        <select
                                            value={createForm.paymentMethod}
                                            onChange={(e) => setCreateForm({ ...createForm, paymentMethod: e.target.value })}
                                        >
                                            <option value="cash">Cash</option>
                                            <option value="bank">Bank</option>
                                            <option value="pending">Pending</option>
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>Payment Status</label>
                                        <select
                                            value={createForm.paymentStatus}
                                            onChange={(e) => setCreateForm({ ...createForm, paymentStatus: e.target.value })}
                                        >
                                            <option value="paid">Paid</option>
                                            <option value="unpaid">Unpaid</option>
                                            <option value="partially_paid">Partially Paid</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className={styles.formGroup}>
                                <label>Expense Date</label>
                                <DatePicker
                                    selected={createForm.ExpDate}
                                    onChange={(date) => setCreateForm({ ...createForm, ExpDate: date })}
                                    dateFormat="dd/MM/yyyy"
                                    className={styles.datePickerInput}
                                />
                                <small>If you want to set any previous date then select from above, by default it will pick today's date</small>
                            </div>

                            <div className={styles.modalActions}>
                                <button type="button" className={styles.cancelBtn} onClick={closeCreateModal}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles.submitBtn}>
                                    Add Expense
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Expense Modal */}
            {showEditModal && selectedExpense && (
                <div className={styles.modalOverlay} onClick={closeEditModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>Edit Expense</h2>
                            <button className={styles.closeBtn} onClick={closeEditModal}>
                                <AiOutlineClose />
                            </button>
                        </div>

                        <form onSubmit={handleEditSubmit} className={styles.modalForm}>
                            <div className={styles.formGroup}>
                                <label>Expense Title *</label>
                                <input
                                    type="text"
                                    value={editForm.Title}
                                    onChange={(e) => setEditForm({ ...editForm, Title: e.target.value })}
                                    placeholder="Example: Office Rent"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Expense Amount *</label>
                                <input
                                    type="number"
                                    value={editForm.Amount}
                                    onChange={(e) => setEditForm({ ...editForm, Amount: e.target.value })}
                                    placeholder="Example: 5000"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Expense ID *</label>
                                <input
                                    type="text"
                                    value={editForm.ExpenseId}
                                    onChange={(e) => setEditForm({ ...editForm, ExpenseId: e.target.value })}
                                    placeholder="Example: INV-2024-001"
                                    required
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Description *</label>
                                <textarea
                                    value={editForm.Description}
                                    onChange={(e) => setEditForm({ ...editForm, Description: e.target.value })}
                                    placeholder="Example: Monthly office rent payment"
                                    rows={3}
                                    required
                                />
                            </div>

                            {isFeatureEnabled('enableCashFlow') && (
                                <>
                                    <div className={styles.formGroup}>
                                        <label>Payment Method</label>
                                        <select
                                            value={editForm.paymentMethod}
                                            onChange={(e) => setEditForm({ ...editForm, paymentMethod: e.target.value })}
                                        >
                                            <option value="cash">Cash</option>
                                            <option value="bank">Bank</option>
                                            <option value="pending">Pending</option>
                                        </select>
                                    </div>

                                    <div className={styles.formGroup}>
                                        <label>Payment Status</label>
                                        <select
                                            value={editForm.paymentStatus}
                                            onChange={(e) => setEditForm({ ...editForm, paymentStatus: e.target.value })}
                                        >
                                            <option value="paid">Paid</option>
                                            <option value="unpaid">Unpaid</option>
                                            <option value="partially_paid">Partially Paid</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            <div className={styles.modalActions}>
                                <button type="button" className={styles.cancelBtn} onClick={closeEditModal}>
                                    Cancel
                                </button>
                                <button type="submit" className={styles.submitBtn}>
                                    Update Expense
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Expense Modal */}
            {showDeleteModal && deletingExpense && (
                <div className={styles.modalOverlay} onClick={closeDeleteModal}>
                    <div className={styles.deleteModalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.deleteModalHeader}>
                            <div className={styles.deleteIcon}>
                                <AiOutlineDelete />
                            </div>
                            <div className={styles.deleteHeaderText}>
                                <h2>Delete Expense</h2>
                                <p>This action cannot be undone</p>
                            </div>
                        </div>

                        <div className={styles.deleteModalBody}>
                            <p>Are you sure you want to delete this expense? All expense data will be permanently removed.</p>

                            <div className={styles.deleteExpenseInfo}>
                                <p><strong>Title:</strong> {deletingExpense.Title}</p>
                                <p><strong>Amount:</strong> {formatCurrency(deletingExpense.Amount)}</p>
                                <p><strong>Expense ID:</strong> {deletingExpense.ExpenseId}</p>
                                <p><strong>Date:</strong> {Moment(deletingExpense.Date).format('DD MMM YYYY')}</p>
                            </div>
                        </div>

                        <div className={styles.deleteModalActions}>
                            <button className={styles.cancelBtn} onClick={closeDeleteModal}>
                                Cancel
                            </button>
                            <button className={styles.deleteConfirmBtn} onClick={handleDeleteConfirm}>
                                <AiOutlineDelete /> Delete Expense
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Expense;
